import { useState } from "react";

import api from "shared/infra/services/api";

import { captureException, captureMessage } from "@sentry/nextjs";

import { ISectionContent } from "../types";
import useBillboardPageContext from "./useBillboardPageContext";

const useBrowsePageService = () => {
  const { setSections } = useBillboardPageContext();

  const [isLoading, setIsLoading] = useState(false);

  const [page, setPage] = useState(2);
  const [isLastPage, setIsLastPage] = useState(false);

  const [sectionsObserverPage, setSectionsObserverPage] = useState(2);
  const [isSectionsObserverLastPage, setIsSectionsObserverLastPage] =
    useState(false);

  async function fetchSectionContent({ sectionId, setSectionContent }) {
    try {
      if (!sectionId || !setSectionContent)
        throw new Error("SectionId or setSectionContent must be provided.");

      setIsLoading(true);

      const response = await api.get(
        `billboard/sections/${sectionId}/content?perPage=10&page=${page}`,
      );

      const lastPage = response?.data?.meta?.last_page;
      setPage((page) => page + 1);
      setIsLastPage(() => page >= lastPage);

      if (response?.data?.data?.length === 0) return null;

      setSectionContent((prev: ISectionContent[]) =>
        prev.concat(response?.data?.data),
      );
    } catch (error) {
      captureException(error);
      captureMessage(
        "Error ~ useBrowsePageService::fetchSectionContent",
        error,
      );
      console.log("Error (fetchSectionContent): ", error);
      setSectionContent([]);
    } finally {
      setIsLoading(false);
    }
  }

  async function getSectionsObserver() {
    try {
      if (!setSections) return;

      setIsLoading(true);

      const response = await api.get(
        `billboard/sections?perPage=100&page=${sectionsObserverPage}`,
      );

      const lastPage = response?.data?.meta?.last_page;
      setIsSectionsObserverLastPage(() => sectionsObserverPage >= lastPage);
      setSectionsObserverPage((page) => page + 1);

      setSections((prev) => prev?.concat(response?.data?.data));
    } catch (error) {
      captureException(error);
      captureMessage("Error useBrowsePageService::getSectionsObserver");
      console.log("Error (getSectionsObserver): ", error);
      setSections([]);
    } finally {
      setIsLoading(false);
    }
  }

  return {
    getSectionsObserver,
    sectionsObserverPage,
    isSectionsObserverLastPage,
    fetchSectionContent,
    page,
    isLastPage,
    isLoading,
  };
};

export default useBrowsePageService;
